import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { securePut, api, secureGet } from '../../config/api';
import { getResponseData } from '../../config/api';
import { formatAmount } from '../../utils/format';
import { getJwkKeystore } from '../../utils/encryption';
import { SaveCancel } from '../../components/PrimaryButton';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import Input from '../../components/Input';
import LoadingLogo from '../../components/LoadingLogo';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from '../../components/Breadcrumbs';

import DeleteIcon from '../../icons/trashR.svg';

const PAYMENT_CODE_CASH = 'CASH';

const PaymentsForm = ({
  title,
  baseFields = [],
  fileFields = [],
  onSuccess,
}) => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState({});
  const [payment, setPayment] = useState({});
  const [files, setFiles] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [previews, setPreviews] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const cashPaymentTypeId = React.useMemo(() => {
    const field = baseFields.find((f) => f.name === 'paymentTypeId');
    const option = field?.options?.find((o) => String(o.code || '').toUpperCase() === PAYMENT_CODE_CASH);
    return option?.value;
  }, [baseFields]);

  useEffect(() => {
    const checkDataReadiness = () => {
      const baseFieldsReady = baseFields.every((field) => {
        if (!field.options) return true;
        if (field.name === 'billingInvoiceId') return true;
        return field.options.length > 0;
      });

      if (id) {
        return initialValues && Object.keys(initialValues).length > 0 && baseFieldsReady;
      }
      return baseFieldsReady;
    };

    if (checkDataReadiness()) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [initialValues, baseFields, id]);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchPaymentData = async () => {
      if (id) setLoading(true);
      try {
        const response = await secureGet(`/payments/${id}`, showSnackbar, { signal: abortController.signal });
        const data = response.data;
        setPayment(data);

        const mappedValues = {
          id: data.id,
          grayClientId: data.grayClientId ,
          paymentTypeId: data.paymentTypeId ?? data.type?.id,
          bankId: data.bankId ?? data.bank?.id,
          billingInvoiceId: data.billingInvoiceId ?? data.billingInvoice?.id ?? data.invoiceId ?? '',
          paymentNumber: data.paymentNumber ?? data.number ?? data.reference,
          reference: data.reference,
          amount: data.amount,
          paymentDate: data.paymentDate ?? data.payment_date ?? data.echeance,
          comment: data.comment ?? data.note,
          image: data.image,
        };

        setInitialValues(mappedValues);
        setFormValues(mappedValues);

      } catch (error) {
        console.error('Error fetching payment data:', error);
        showSnackbar(t('errors.fetch_payment'), 'error');
        if (id) setLoading(false);
      }
    };

    if (id) fetchPaymentData();
    return () => {
      abortController.abort();
    };
  }, [id]);

  useEffect(() => {
    const grayClientId = formValues.grayClientId != null && formValues.grayClientId !== '' ? formValues.grayClientId : null;
    if (!grayClientId) {
      setInvoiceOptions([]);
      return;
    }
    const ac = new AbortController();
    setLoadingInvoices(true);
    secureGet(`/billing-invoices/by-grayClient/${grayClientId}?status=ISSUED&status=OVERDUE`, showSnackbar, { signal: ac.signal })
      .then((res) => {
        const list = res?.data?.content ?? res?.data ?? [];
        const options = (Array.isArray(list) ? list : []).map((inv) => {
          const ttc = inv.priceTTC ?? inv.totalTTC;
          return {
            value: inv.id,
            label: inv.invoiceNumber ?? inv.number ?? String(inv.id),
            priceTTC: ttc != null && ttc !== '' ? Number(ttc) : null,
          };
        });
        setInvoiceOptions(options);
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setInvoiceOptions([]);
      })
      .finally(() => setLoadingInvoices(false));
    return () => ac.abort();
  }, [formValues.grayClientId]);


  useEffect(() => {
    if (initialValues.image) {
      const match = initialValues.image.match(/grayfox\/([^?]+)/);
      if (match) {
        setFileNames((prev) => ({
          ...prev,
          image: match[1],
        }));
      }
    }
  }, [initialValues.image]);


  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const handleInputChange = (name, value) => {
    setFormValues((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'grayClientId') {
        next.billingInvoiceId = '';
      }
      return next;
    });
  };

  const handleFileChange = (fieldName, file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreviews(prev => ({ ...prev, [fieldName]: previewUrl }));
      setFiles(prev => ({ ...prev, [fieldName]: file }));
      setFileNames(prev => ({ ...prev, [fieldName]: file.name }));
    }
  };

  const removeFile = (fieldName) => {
    URL.revokeObjectURL(previews[fieldName]);
    setPreviews(prev => ({ ...prev, [fieldName]: undefined }));
    setFiles(prev => ({ ...prev, [fieldName]: undefined }));
    setFileNames(prev => ({ ...prev, [fieldName]: undefined }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  const requiredFields = [...baseFields, ...fileFields];
  const isCash = formValues.paymentTypeId === cashPaymentTypeId;
  const missing = requiredFields.filter(f => {
    const required = f.name === 'paymentNumber' ? !isCash : f.required;
    return required && !formValues[f.name];
  });
  if (missing.length > 0) {
      showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
      return;
  }
  
  const uploadedFileNames = {};
  let key;
  try {
    if (fileFields.length > 0 && Object.keys(files).length > 0) {
      key = await getJwkKeystore();

      for (const [fieldName, file] of Object.entries(files)) {
        const formData = new FormData();
        formData.append('file', file);

        const fileUploadResponse = await api.post(
          '/attachments/images/add',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: localStorage.getItem('token'),
            },
          }
        );

        const decryptedResponse = await getResponseData(fileUploadResponse, key);
        if (decryptedResponse?.fileName) {
          uploadedFileNames[fieldName] = decryptedResponse.fileName;
        }
      }

    }
  } catch (error) {
    console.error('Attachment upload failed:', error);
    showSnackbar(t('errors.generic'), 'error');
    return; 
  }

  try {
    let imageValue = '';
    if (uploadedFileNames.image) {
      imageValue = uploadedFileNames.image;
    } else if (formValues.image) {
      const match = formValues.image.match(/grayfox\/([^?]+)/);
      imageValue = match ? match[1] : formValues.image;
    } else if (initialValues.image) {
      imageValue = initialValues.image;
    }

    const toNum = (v) => (v != null && v !== '' ? Number(v) : undefined);
    const toStr = (v) => (v != null && v !== '' ? String(v) : undefined);
    const toISODate = (v) => {
      if (v == null || v === '') return undefined;
      if (typeof v === 'object' && v.date) return v.date;
      if (typeof v === 'string' && v.includes('T')) return v;
      if (typeof v === 'string') return v;
      return undefined;
    };

    const payload = {
      grayClientId: toNum(formValues.grayClientId),
      amount: toNum(formValues.amount),
      bankId: toNum(formValues.bankId),
      billingInvoiceId: toNum(formValues.billingInvoiceId),
      comment: toStr(formValues.comment),
      image: toStr(imageValue),
      paymentDate: toISODate(formValues.paymentDate),
      paymentNumber: toStr(formValues.paymentNumber),
      paymentTypeId: toNum(formValues.paymentTypeId),
      reference: toStr(formValues.reference),
    };
    if (formValues.id != null && formValues.id !== '') {
      payload.id = Number(formValues.id);
    }
    const cleanPayload = Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== undefined && v !== '')
    );

    console.log(cleanPayload);

    await securePut('/payments/add', cleanPayload, showSnackbar);
    onSuccess();
    history.push({
      pathname: '/payments',
      state: {
        filters: location.state?.filters || {},
        page: location.state?.page || 0,
        size: location.state?.size || 30,
      },
    });
  } catch (error) {
    console.error('Payment submission failed:', error);
  }
};


  const handleCancel = () => {
    if (location.pathname.includes("details")) {
      history.push(`/payments/details/${payment.id}`);
    } else {
      history.push({
        pathname: "/payments",
        state: {
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
        },
      });
    }
  };

  const filteredFields = formValues.paymentTypeId === cashPaymentTypeId
    ? baseFields.filter((field) => field.name !== "bankId")
    : baseFields;

  const fieldsWithInvoiceOptions = filteredFields.map((field) =>
    field.name === 'billingInvoiceId'
      ? { ...field, options: invoiceOptions }
      : field
  );

  const selectedInvoiceTotalTTC = React.useMemo(() => {
    const id = formValues.billingInvoiceId;
    if (id == null || id === "") return null;
    const match = invoiceOptions.find((o) => String(o.value) === String(id));
    const ttc = match?.priceTTC;
    if (ttc == null || Number.isNaN(ttc)) return null;
    return ttc;
  }, [formValues.billingInvoiceId, invoiceOptions]);

  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.payments")}</title>
      </Helmet>
      <Breadcrumbs />
      <h2 className="text-[32px] text-[#38485C] font-semibold mb-4">{title}</h2>

      {loading ? (
        <div className="flex justify-center items-center h-[300px]">
          <LoadingLogo />
        </div>
      ) : (

        <form className="space-y-6">
          {/* Base Fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {fieldsWithInvoiceOptions.map((field, index) => (
              <Input
                key={index}
                type={field.type || "text"}
                label={field.label}
                name={field.name}
                value={formValues[field.name] ?? ""}
                options={field.options || []}
                required={field.name === 'paymentNumber' ? (formValues.paymentTypeId != null && formValues.paymentTypeId !== cashPaymentTypeId) : field.required}
                disabled={field.name === 'billingInvoiceId' && loadingInvoices}
                onChange={(value) => handleInputChange(field.name, value)}
              />
              ))}
            </div>
          </div>

          {/* File Fields Section */}
          {fileFields && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {fileFields.map((field, index) => {
                  const hasFile = Boolean(fileNames[field.name]);
                  const existingImage = initialValues[field.name];
                  const previewImage = previews[field.name];

                  return (
                    <div key={index} className="space-y-4">
                      <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
                        {field.label} <span className="text-red-500">*</span>
                      </h3>
                      <label
                        htmlFor={`fileInput-${field.name}`}
                        className={` border-2 border-dashed h-[93px] rounded-md p-4 block cursor-pointer transition-colors
                ${hasFile ? 'border-gray-300' : 'border-gray-300'} `}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const droppedFile = e.dataTransfer.files?.[0];
                          if (droppedFile) {
                            const validTypes = field.accept.split(',');
                            const isValid = validTypes.some(type => {
                              if (type === 'image/*') return droppedFile.type.startsWith('image/');
                              return droppedFile.type === type;
                            });

                            if (isValid) {
                              handleFileChange(field.name, droppedFile);
                            } else {
                              setSnackbar(`Invalid file type. Allowed: ${field.accept}`);
                            }
                          }
                        }}
                      >
                        <input
                          id={`fileInput-${field.name}`}
                          type="file"
                          name={field.name}
                          accept={field.accept}
                          className="hidden"
                          onChange={(e) => {
                            const selectedFile = e.target.files?.[0];
                            if (selectedFile) {
                              const validTypes = field.accept.split(',');
                              const isValid = validTypes.some(type => {
                                if (type === 'image/*') return selectedFile.type.startsWith('image/');
                                return selectedFile.type === type;
                              });

                              if (isValid) {
                                handleFileChange(field.name, selectedFile);
                                e.target.value = null;
                              } else {
                                setSnackbar(`Invalid file type. Allowed: ${field.accept}`);
                                e.target.value = null;
                              }
                            }
                          }}
                        />

                        <div className="flex items-center justify-between gap-3 h-full min-h-[40px]">
                          {hasFile ? (
                            <>
                              <div className="flex items-center ml-[40px] min-w-0 flex-1 h-full">
                                {(previewImage || existingImage) && (
                                  <div className="relative w-20 h-20 shrink-0">
                                    <img
                                      src={previewImage || existingImage}
                                      alt="Preview"
                                      className="w-full h-full object-contain rounded-md"
                                    />
                                  </div>
                                )}
                                <span className="text-[14px] font-semibold ml-[20px] text-[#38475D] truncate">
                                  {fileNames[field.name]}
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  removeFile(field.name);
                                }}
                                className="w-8 h-8 p-1 -m-1 mr-[36px] flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                              >
                                <img src={DeleteIcon} alt="Delete" className="w-5 h-5" />
                              </button>
                            </>
                          ) : (
                            <div className="flex items-center justify-center w-full gap-4 text-center">
                              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <div className="text-left">
                                <p className="text-sm text-gray-500">{field.placeholderText1}</p>
                                <p className="text-sm font-bold text-blue-500">{field.placeholderText2}</p>
                              </div>
                            </div>
                          )}
                        </div>

                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 mt-8">
            <div className="min-w-0 text-[16px]">
              {selectedInvoiceTotalTTC != null && (
                <p className="flex flex-wrap items-baseline gap-x-2 text-[#38485C]">
                  <span className="font-medium">{t("columns.montant")}: </span>
                  <span className="font-bold text-[#043F88] tabular-nums">
                    {formatAmount(selectedInvoiceTotalTTC)}
                  </span>
                </p>
              )}
            </div>
            <div className="shrink-0">
              <SaveCancel
                onSaveClick={handleSubmit}
                onCancelClick={handleCancel}
              />
            </div>
          </div>
        </form>
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default PaymentsForm;